import IconChevronDown from "@shared/components/icons/ChevronDown"
import IconListBullets1 from "@shared/components/icons/ListBullets1"
import IconListNumbers from "@shared/components/icons/ListNumbers"
import IconListStars from "@shared/components/icons/ListStars"
import { Button } from "@shared/components/untitledui/base/buttons/button"
import { Dropdown } from "@shared/components/untitledui/base/dropdown/dropdown"
import { Tooltip } from "@shared/components/untitledui/base/tooltip/tooltip"
import { cx } from "@shared/components/untitledui/util/cx"
import { OPERATION_CONTRACTS } from "@shared/unified-tiptap/analytics/operations"
import type { ListKind } from "@shared/unified-tiptap/analytics/types"
import {
  isBlockTypeActive,
  setBlockType,
  toggleBlockType,
} from "@shared/unified-tiptap/util/block-node-helpers"
import { modKeyText } from "@shared/unified-tiptap/util/is-apple"
import type { Editor } from "@tiptap/core"
import { useEditorState } from "@tiptap/react"
import { Pilcrow02 } from "@untitledui/icons"
import type { ReactNode } from "react"
import type { Key, Selection } from "react-aria-components"
import type { FlatListType } from "tiptap-extension-flat-list"
import {
  MENU_ROW_GLYPH,
  SELECTED_ROW,
  TOOLBAR_BUTTON_ACTIVE,
  TOOLBAR_BUTTON_CHROME,
  TOOLBAR_CHEVRON,
} from "./toolbar-button-chrome"

// Like the heading dropdown, a single-selection menu: each list type is a key, the active
// list reads aria-checked, and deselecting it (or picking "Normal Text") falls back to a paragraph.
const LIST_ITEMS: {
  kind: Exclude<ListKind, "none">
  key: FlatListType
  label: string
  glyph: ReactNode
  shortcut: string
}[] = [
  {
    kind: "bulleted",
    key: "flatListItemUnordered",
    label: "Bulleted list",
    glyph: <IconListStars />,
    shortcut: modKeyText + "+Shift+7",
  },
  {
    kind: "numbered",
    key: "flatListItemOrdered",
    label: "Numbered list",
    glyph: <IconListNumbers />,
    shortcut: modKeyText + "+Shift+8",
  },
  {
    kind: "checklist",
    key: "flatListItemTask",
    label: "To-do list",
    glyph: <IconListBullets1 />,
    shortcut: modKeyText + "+Shift+9",
  },
]

/** The active list type, or null when the block is a paragraph / something else. */
function activeListType(editor: Editor): FlatListType | null {
  return (
    LIST_ITEMS.map((item) => item.key).find((type) => isBlockTypeActive(editor.state, type)) ?? null
  )
}

export function ListDropdownMenu({ editor }: { editor: Editor }) {
  const activeType = useEditorState({ editor, selector: () => activeListType(editor) })
  const isParagraph = useEditorState({
    editor,
    selector: (snapshot) => isBlockTypeActive(snapshot.editor.state, "paragraph"),
  })
  const canToggle = useEditorState({
    editor,
    selector: (snapshot) =>
      LIST_ITEMS.some((item) =>
        snapshot.editor.can().command(toggleBlockType(item.key, "paragraph")),
      ),
  })

  // Selected key: the active list, else "none" when it's a paragraph, else nothing (mixed).
  const selectedKey: Key | null = activeType ?? (isParagraph ? "none" : null)

  const onSelectionChange = (keys: Selection) => {
    const key = keys === "all" ? undefined : [...keys][0]
    const list = LIST_ITEMS.find((item) => item.key === key)
    const chain = editor
      .chain()
      .label(OPERATION_CONTRACTS.OP_SET_LIST, "floating-toolbar", { list: list?.kind ?? "none" })
    if (list) chain.command(setBlockType(list.key)).run()
    // undefined (deselected the active list) or "none": land on a plain paragraph.
    else chain.setNode("paragraph").run()
  }

  return (
    <Dropdown.Root>
      <Tooltip title="Lists" closeDelay={0}>
        <Button
          isDisabled={!canToggle}
          color="tertiary"
          size="xs"
          className={cx(
            "data-icon-only:px-0",
            TOOLBAR_BUTTON_CHROME,
            activeType !== null && TOOLBAR_BUTTON_ACTIVE,
          )}
          aria-label="List options"
          iconLeading={
            LIST_ITEMS.find((item) => item.key === (activeType ?? "flatListItemUnordered"))!.glyph
          }
          iconTrailing={<IconChevronDown className={TOOLBAR_CHEVRON} />}
        />
      </Tooltip>
      <Dropdown.Popover placement="bottom">
        <Dropdown.Menu
          aria-label="List style"
          selectionMode="single"
          selectedKeys={selectedKey === null ? [] : [selectedKey]}
          onSelectionChange={onSelectionChange}
        >
          {LIST_ITEMS.map((item) => (
            <ListItem key={item.key} id={item.key} label={item.label} shortcut={item.shortcut}>
              {item.glyph}
            </ListItem>
          ))}
          <ListItem id="none" label="Normal Text" shortcut={`${modKeyText}+Alt+0`}>
            <Pilcrow02 />
          </ListItem>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  )
}

function ListItem({
  id,
  label,
  shortcut,
  children,
}: {
  id: Key
  label: string
  shortcut: string
  children: ReactNode
}) {
  return (
    <Dropdown.Item id={id} textValue={label} aria-label={label} selectionIndicator="none">
      {({ isSelected }) => (
        <span
          className={cx("flex w-full items-center justify-between gap-4", SELECTED_ROW(isSelected))}
        >
          <span className={cx("flex items-center gap-2", MENU_ROW_GLYPH)}>
            {children} {label}
          </span>
          <span className="text-gray-400">{shortcut}</span>
        </span>
      )}
    </Dropdown.Item>
  )
}
